import { DecimalPipe, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { SlKpiCardComponent } from '@brand-lolly/app/components/sl-kpi-card/sl-kpi-card.component';
import { AiSummaryLanguageTableComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/pages/ai-summary/components/ai-summary-language-table.component';
import { AiSummaryRatingDistributionComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/pages/ai-summary/components/ai-summary-rating-distribution.component';
import { AiSummarySignalCardComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/pages/ai-summary/components/ai-summary-signal-card.component';
import { AiSummarySourcesComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/pages/ai-summary/components/ai-summary-sources.component';
import { IconComponent, TypographyComponent } from 'components';
import {
	AiSummaryContent,
	AiSummaryConversationItem,
	AiSummaryEntityType,
	AiSummaryPositioningRow,
	AiSummaryShareOfVoiceRow,
	AiSummaryLanguageTerm,
	AiSummaryMentionHighlight,
	AiSummaryMetric,
	AiSummaryRatingDistributionItem,
	AiSummaryReviewHighlight,
	AiSummarySignal
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-ai-summary.model';

interface TextSegment {
	text: string;
	bold: boolean;
	italic: boolean;
}

const KPI_SECTION_TITLES: Record<AiSummaryEntityType, string> = {
	brand: 'Brand health',
	competitor_project: 'Competitive landscape',
	industry: 'Industry pulse'
};

@Component({
	standalone: true,
	selector: 'app-ai-summary-content',
	imports: [
		NgIf,
		NgFor,
		TitleCasePipe,
		DecimalPipe,
		SlKpiCardComponent,
		AiSummaryLanguageTableComponent,
		AiSummaryRatingDistributionComponent,
		AiSummarySourcesComponent,
		AiSummarySignalCardComponent,
		TypographyComponent,
		IconComponent
	],
	templateUrl: './ai-summary-content.component.html',
	styleUrls: ['./ai-summary-content.component.scss']
})
export class AiSummaryContentComponent implements OnChanges {
	@Input() content: AiSummaryContent | null = null;
	@Input() imageUrl: string | null = null;
	@Input() entityType: AiSummaryEntityType = 'brand';

	kpis: AiSummaryMetric[] = [];
	unfoldedText: string = '';
	reviewsBlock: AiSummaryContent['reviews'] | null = null;
	isReviewsEnabled: boolean = false;
	showRatingDistribution: boolean = false;
	/** Reviews are enabled but the block has nothing to draw. */
	reviewsEmpty: boolean = false;
	/** No section at all would render. */
	isEmpty: boolean = false;
	ratingDistribution: AiSummaryRatingDistributionItem[] = [];
	highlightReviewStars: boolean[] = [];
	conversationItems: AiSummaryConversationItem[] = [];
	languageTerms: AiSummaryLanguageTerm[] = [];
	languageTextSegments: TextSegment[] = [];
	topMention: AiSummaryMentionHighlight | null = null;
	topReview: AiSummaryReviewHighlight | null = null;
	mentionIsNegative: boolean = false;
	reviewIsNegative: boolean = false;
	topMentionLikesFormatted: string = '';
	topReviewStars: boolean[] = [];
	sources: AiSummaryContent['sources'] | null = null;
	sourcesTextSegments: TextSegment[] = [];
	signals: AiSummarySignal[] = [];
	oneParagraph: string = '';
	oneParagraphSegments: TextSegment[] = [];
	kpiSectionTitle: string = 'Brand health';
	shareOfVoiceRows: AiSummaryShareOfVoiceRow[] = [];
	positioningRows: AiSummaryPositioningRow[] = [];

	ngOnChanges(): void {
		const c = this.content;

		this.kpis = c?.kpis ?? [];
		this.unfoldedText = c?.unfolded?.text ?? '';
		this.reviewsBlock = c?.reviews ?? null;
		this.isReviewsEnabled =
			!!this.reviewsBlock && this.reviewsBlock.enabled !== false;
		this.ratingDistribution = this.reviewsBlock?.rating_distribution ?? [];
		this.highlightReviewStars = this.toStarsArray(
			this.reviewsBlock?.standout_review?.rating ?? null
		);
		this.conversationItems = c?.moved_conversation?.items ?? [];
		this.languageTerms = c?.language_people_use?.terms ?? [];
		this.languageTextSegments = this.parseInlineFormatting(
			c?.language_people_use?.text ?? ''
		);
		this.topMention = c?.standout_voices?.top_mention ?? null;
		this.topReview = c?.standout_voices?.top_review ?? null;
		this.mentionIsNegative = this.topMention?.sentiment === 'negative';
		this.reviewIsNegative = this.topReview?.sentiment === 'negative';
		this.topMentionLikesFormatted = this.formatLikes(
			this.topMention?.likes ?? 0
		);
		this.topReviewStars = this.toStarsArray(this.topReview?.rating ?? null);
		this.sources = c?.sources ?? null;
		this.sourcesTextSegments = this.parseInlineFormatting(
			c?.sources?.text ?? ''
		);
		this.signals = c?.signals ?? [];
		this.oneParagraph = c?.one_paragraph ?? '';
		this.oneParagraphSegments = this.parseInlineFormatting(
			this.oneParagraph
		);
		this.kpiSectionTitle = KPI_SECTION_TITLES[this.entityType];
		this.shareOfVoiceRows = c?.share_of_voice?.rows ?? [];
		this.positioningRows = c?.positioning?.rows ?? [];

		this.showRatingDistribution =
			this.ratingDistribution.length > 0 &&
			this.reviewsBlock?.average_rating != null;
		this.reviewsEmpty =
			this.isReviewsEnabled &&
			!this.showRatingDistribution &&
			!this.reviewsBlock?.text &&
			!this.reviewsBlock?.standout_review;
		// Mirrors the template's section guards one for one.
		const hasSection =
			!!this.oneParagraph ||
			this.kpis.length > 0 ||
			this.shareOfVoiceRows.length > 0 ||
			this.positioningRows.length > 0 ||
			!!this.imageUrl ||
			!!this.unfoldedText ||
			this.isReviewsEnabled ||
			this.conversationItems.length > 0 ||
			this.languageTerms.length > 0 ||
			!!this.topMention ||
			!!this.topReview ||
			!!this.sources?.channels?.length ||
			!!this.sources?.review_sources?.length ||
			this.signals.length > 0;
		this.isEmpty = !c || !hasSection;
	}

	private toStarsArray(rating: number | null): boolean[] {
		const filled = Math.round(rating ?? 0);
		return Array.from({ length: 5 }, (_, i) => i < filled);
	}

	private formatLikes(n: number): string {
		if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M views';
		if (n >= 1000) return Math.round(n / 1000) + 'K views';
		return n + ' views';
	}

	private parseInlineFormatting(text: string): TextSegment[] {
		return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map(part => {
			if (part.startsWith('**') && part.endsWith('**')) {
				return { text: part.slice(2, -2), bold: true, italic: false };
			}
			if (part.startsWith('*') && part.endsWith('*')) {
				return { text: part.slice(1, -1), bold: false, italic: true };
			}
			return { text: part, bold: false, italic: false };
		});
	}
}
